import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Nav } from "../components/Nav";
import { Footer } from "../components/Footer";
import { Reveal, Eyebrow } from "../components/Reveal";
import { JOURNAL_ARTICLES } from "../data/journal";

const Rich = ({ text }) => {
  const parts = text.split(/(\*\*.+?\*\*|\*.+?\*)/g).filter(Boolean);
  return (
    <>
      {parts.map((p, i) => {
        if (p.startsWith("**") && p.endsWith("**"))
          return <strong key={i} className="text-ink font-semibold">{p.slice(2, -2)}</strong>;
        if (p.startsWith("*") && p.endsWith("*") && p.length > 2)
          return <em key={i}>{p.slice(1, -1)}</em>;
        return p;
      })}
    </>
  );
};

export default function JournalArticle() {
  const { slug } = useParams();
  const article = JOURNAL_ARTICLES.find((a) => a.slug === slug && a.published);

  useEffect(() => {
    document.title = article
      ? `${article.title} — The InSight Journal`
      : "The InSight Journal — InSight Metabolics";
    return () => {
      document.title = "InSight Metabolics — A Precision Metabolic & Lifestyle Clinic";
    };
  }, [article]);

  return (
    <div data-testid="journal-article-page">
      <Nav />
      <main className="bg-ivory grain relative min-h-screen">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-36 pb-24">
          {!article ? (
            <div className="text-center py-20" data-testid="journal-article-missing">
              <h1 className="font-serif text-3xl text-forest">This essay isn't available yet.</h1>
              <p className="mt-4 text-inkmuted">It may still be forthcoming. Please check back soon.</p>
              <Link to="/#journal" className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-forest">
                <ArrowLeft size={15} /> Back to The InSight Journal
              </Link>
            </div>
          ) : (
            <Reveal>
              <Eyebrow>{article.category}</Eyebrow>
              <h1 className="mt-6 font-serif font-medium text-forest text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight" data-testid="journal-article-title">
                {article.title}
              </h1>
              <p className="mt-4 font-mono text-[11px] tracking-[0.22em] uppercase text-inkmuted">
                The InSight Journal · Dr Rohan Dikshit
              </p>
              <div className="mt-12 space-y-10">
                {article.content.map((block, i) => (
                  <section key={i}>
                    {block.heading && (
                      <h2 className="font-serif text-2xl text-ink mb-4">{block.heading}</h2>
                    )}
                    {block.paragraphs.map((p, j) => (
                      <p key={j} className="text-inkmuted leading-relaxed text-[15px] sm:text-base mb-4">
                        <Rich text={p} />
                      </p>
                    ))}
                    {block.items && (
                      <ul className="space-y-2.5 mb-4">
                        {block.items.map((it, j) => (
                          <li key={j} className="flex items-start gap-3 text-inkmuted leading-relaxed text-[15px] sm:text-base italic">
                            <span className="mt-[9px] h-1 w-1 rotate-45 bg-gold shrink-0" />
                            <Rich text={it} />
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>
                ))}
              </div>
              <p className="mt-14 text-xs text-inkmuted/80 border-l-2 border-gold/60 pl-5 leading-relaxed">
                This essay is for general education and does not constitute medical advice. Recommendations are
                always personalised according to medical history, investigations and clinical needs.
              </p>
            </Reveal>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
